import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { animate } from "motion"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "../../lib/utils"

const Tabs = TabsPrimitive.Root

const tabsListSecondaryNavClass =
  "flex items-center justify-start gap-0.5 sm:gap-1 h-9 sm:h-12 rounded-full border border-white/30 bg-white/30 px-1.5 sm:px-2 py-0 text-muted-foreground shadow-sm backdrop-blur-lg overflow-x-auto scrollbar-hide transition-[border-radius,border-color] duration-200 ease-out"
const tabsTriggerSecondaryNavClass =
  "inline-flex items-center justify-center whitespace-nowrap h-6 sm:h-8 rounded-full px-2.5 sm:px-4 text-xs sm:text-sm font-medium leading-none text-muted-foreground transition-[background,box-shadow,transform,color,font-weight,border-color] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] hover:text-[var(--color-ink)] data-[state=active]:[background:linear-gradient(135deg,var(--color-lemon)_30%,var(--color-mint))] data-[state=active]:text-[var(--color-ink)] data-[state=active]:font-semibold data-[state=active]:border data-[state=active]:border-[#C0D9AB] data-[state=active]:shadow-[var(--shadow-yellow-sm)] data-[state=active]:animate-[nav-active-fade_240ms_ease]"

const TAB_SNAP_THRESHOLD = 0.3
const TAB_VELOCITY_THRESHOLD = 400
const TAB_RUBBER_BAND = 0.3
const TAB_SPRING = { type: 'spring', stiffness: 300, damping: 30, mass: 1 }
const TAB_GAP = 16
const TAB_HINT_PROGRESS = 0.55

/**
 * Direct-manipulation swipeable tabs wrapper.
 * Pre-renders prev + current + next tab panels in a three-panel track
 * so content follows the finger during a swipe.
 *
 * Uses an internal `contentTab` to drive panel rendering independently
 * of the Radix `activeTab` (which drives the nav pill styling).
 * This lets us update the pill early (at ~55% progress) while the
 * track reset only happens when the content actually swaps.
 */
function SwipeableTabsContent({ tabs, activeTab, onTabChange, children, className }) {
  const wrapRef = React.useRef(null)
  const trackRef = React.useRef(null)
  const touchRef = React.useRef({ startX: 0, startY: 0, startTime: 0, currentX: 0, locked: null, animCtrl: null })
  const needsResetRef = React.useRef(false)
  const hintFiredRef = React.useRef(false)

  const [contentTab, setContentTab] = React.useState(activeTab)
  const prevContentTabRef = React.useRef(contentTab)
  const contentTabRef = React.useRef(contentTab)
  const tabsRef = React.useRef(tabs)
  const onTabChangeRef = React.useRef(onTabChange)

  contentTabRef.current = contentTab
  tabsRef.current = tabs
  onTabChangeRef.current = onTabChange

  React.useEffect(() => {
    if (!touchRef.current.animCtrl) {
      setContentTab(activeTab)
    }
  }, [activeTab])

  const childArray = React.useMemo(() => React.Children.toArray(children), [children])

  const childByTab = React.useMemo(() => {
    const map = {}
    childArray.forEach(child => {
      if (React.isValidElement(child) && child.props.value) {
        map[child.props.value] = child
      }
    })
    return map
  }, [childArray])

  const currentIdx = tabs.indexOf(contentTab)

  const getStride = React.useCallback(() => (wrapRef.current?.offsetWidth || 300) + TAB_GAP, [])

  const setTrackX = React.useCallback((x) => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${x}px)`
    }
  }, [])

  React.useLayoutEffect(() => {
    if (prevContentTabRef.current !== contentTab && needsResetRef.current) {
      needsResetRef.current = false
      setTrackX(0)
    }
    prevContentTabRef.current = contentTab
  }, [contentTab, setTrackX])

  React.useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return

    const onTouchStart = (e) => {
      const wasAnimating = !!touchRef.current.animCtrl
      if (touchRef.current.animCtrl) {
        touchRef.current.animCtrl.stop()
        touchRef.current.animCtrl = null
      }

      if (wasAnimating || hintFiredRef.current) {
        const current = contentTabRef.current
        hintFiredRef.current = false
        onTabChangeRef.current(current)
      }

      needsResetRef.current = false
      setTrackX(0)
      const t = e.touches[0]
      touchRef.current.startX = t.clientX
      touchRef.current.startY = t.clientY
      touchRef.current.currentX = 0
      touchRef.current.startTime = Date.now()
      touchRef.current.locked = null
    }

    const onTouchMove = (e) => {
      const t = e.touches[0]
      const dx = t.clientX - touchRef.current.startX
      const dy = t.clientY - touchRef.current.startY

      if (touchRef.current.locked === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          touchRef.current.locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
        }
        return
      }

      if (touchRef.current.locked === 'v') return
      e.preventDefault()

      const currentTabs = tabsRef.current
      const ct = contentTabRef.current
      const idx = currentTabs.indexOf(ct)
      const atFirst = idx <= 0
      const atLast = idx >= currentTabs.length - 1
      let x = dx

      if ((atFirst && dx > 0) || (atLast && dx < 0)) {
        x = dx * TAB_RUBBER_BAND
      }

      const stride = getStride()
      const dragProgress = Math.abs(x) / stride
      const direction = x < 0 ? 1 : -1
      const nextIdx = idx + direction

      if (!hintFiredRef.current && dragProgress >= TAB_HINT_PROGRESS && nextIdx >= 0 && nextIdx < currentTabs.length) {
        hintFiredRef.current = true
        onTabChangeRef.current(currentTabs[nextIdx])
      } else if (hintFiredRef.current && dragProgress < TAB_HINT_PROGRESS) {
        hintFiredRef.current = false
        onTabChangeRef.current(ct)
      }

      touchRef.current.currentX = x
      setTrackX(x)
    }

    const onTouchEnd = () => {
      if (touchRef.current.locked !== 'h') return

      const dx = touchRef.current.currentX
      const stride = getStride()
      const elapsed = (Date.now() - touchRef.current.startTime) / 1000
      const velocity = Math.abs(dx) / Math.max(elapsed, 0.01)
      const w = wrapRef.current?.offsetWidth || 300
      const offsetRatio = Math.abs(dx) / w

      const shouldSnap = offsetRatio > TAB_SNAP_THRESHOLD || velocity > TAB_VELOCITY_THRESHOLD

      if (shouldSnap) {
        const currentTabs = tabsRef.current
        const ct = contentTabRef.current
        const direction = dx < 0 ? 1 : -1
        const idx = currentTabs.indexOf(ct)
        const nextIdx = idx + direction
        if (nextIdx >= 0 && nextIdx < currentTabs.length) {
          const nextTabValue = currentTabs[nextIdx]
          const targetX = -direction * stride

          if (!hintFiredRef.current) {
            onTabChangeRef.current(nextTabValue)
          }
          hintFiredRef.current = false

          const obj = { x: dx }
          touchRef.current.animCtrl = animate(obj, { x: targetX }, {
            ...TAB_SPRING,
            onUpdate: () => setTrackX(obj.x),
            onComplete: () => {
              touchRef.current.animCtrl = null
              needsResetRef.current = true
              setContentTab(nextTabValue)
            },
          })
          return
        }
      }

      if (hintFiredRef.current) {
        hintFiredRef.current = false
        onTabChangeRef.current(contentTabRef.current)
      }

      const obj = { x: dx }
      touchRef.current.animCtrl = animate(obj, { x: 0 }, {
        ...TAB_SPRING,
        onUpdate: () => setTrackX(obj.x),
        onComplete: () => { touchRef.current.animCtrl = null },
      })
    }

    wrap.addEventListener('touchstart', onTouchStart, { passive: true })
    wrap.addEventListener('touchmove', onTouchMove, { passive: false })
    wrap.addEventListener('touchend', onTouchEnd, { passive: true })

    return () => {
      wrap.removeEventListener('touchstart', onTouchStart)
      wrap.removeEventListener('touchmove', onTouchMove)
      wrap.removeEventListener('touchend', onTouchEnd)
    }
  }, [setTrackX, getStride])

  const prevTab = currentIdx > 0 ? tabs[currentIdx - 1] : null
  const nextTab = currentIdx < tabs.length - 1 ? tabs[currentIdx + 1] : null

  const renderPanel = (tabValue) => {
    if (!tabValue) return null
    const child = childByTab[tabValue]
    if (!child) return null
    return React.cloneElement(child, { forceMount: true })
  }

  return (
    <div ref={wrapRef} className={cn(className)} style={{ overflow: 'hidden', width: '100%', touchAction: 'pan-y', overscrollBehaviorX: 'none' }}>
      {/* Only the active panel sits in normal flow, so the container height
          hugs the current tab. The prev/next panels are absolutely positioned
          one stride to either side (off-screen, clipped by the wrap's
          overflow:hidden) — they slide into view as the track is dragged but
          DON'T inflate the height to the tallest tab. */}
      <div
        ref={trackRef}
        style={{ position: 'relative', willChange: 'transform' }}
      >
        {prevTab && (
          <div style={{ position: 'absolute', top: 0, right: `calc(100% + ${TAB_GAP}px)`, width: '100%' }}>
            {renderPanel(prevTab)}
          </div>
        )}
        <div style={{ width: '100%' }}>
          {renderPanel(contentTab)}
        </div>
        {nextTab && (
          <div style={{ position: 'absolute', top: 0, left: `calc(100% + ${TAB_GAP}px)`, width: '100%' }}>
            {renderPanel(nextTab)}
          </div>
        )}
      </div>
    </div>
  )
}

const FADE_WIDTH = 44

function useEdgeFade(listRef) {
  const [fade, setFade] = React.useState({ left: false, right: false })

  React.useEffect(() => {
    const el = listRef.current
    if (!el) return

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el
      setFade({
        left: scrollLeft > 4,
        right: scrollLeft + clientWidth < scrollWidth - 4,
      })
    }

    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [listRef])

  const style = React.useMemo(() => {
    const s = {}
    const { left, right } = fade
    if (!left && !right) return undefined

    if (left && right) {
      s.WebkitMaskImage = s.maskImage =
        `linear-gradient(to right, transparent, black ${FADE_WIDTH}px, black calc(100% - ${FADE_WIDTH}px), transparent)`
    } else if (left) {
      s.WebkitMaskImage = s.maskImage =
        `linear-gradient(to right, transparent, black ${FADE_WIDTH}px)`
    } else {
      s.WebkitMaskImage = s.maskImage =
        `linear-gradient(to left, transparent, black ${FADE_WIDTH}px)`
    }

    const r = 'var(--radius-full, 9999px)'
    s.borderRadius = `${left ? '0' : r} ${right ? '0' : r} ${right ? '0' : r} ${left ? '0' : r}`
    s.borderLeftColor = left ? 'transparent' : undefined
    s.borderRightColor = right ? 'transparent' : undefined

    return s
  }, [fade.left, fade.right])

  return { fade, style }
}

function OverflowChevron({ side, listRef }) {
  const handleClick = () => {
    const el = listRef.current
    if (!el) return
    const delta = side === 'right' ? Math.max(120, el.clientWidth * 0.6) : -Math.max(120, el.clientWidth * 0.6)
    el.scrollBy({ left: delta, behavior: 'smooth' })
  }
  const Icon = side === 'right' ? ChevronRight : ChevronLeft
  return (
    <button
      type="button"
      aria-label={side === 'right' ? 'Scroll tabs right' : 'Scroll tabs left'}
      onClick={handleClick}
      className={cn(
        "pointer-events-auto absolute top-1/2 -translate-y-1/2 z-10",
        "flex items-center justify-center h-6 w-6 sm:h-7 sm:w-7 rounded-full",
        "bg-white shadow-md border border-white/60 text-[var(--color-ink)]",
        "hover:bg-white/95 transition-opacity",
        side === 'right' ? 'right-1' : 'left-1'
      )}
    >
      <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
    </button>
  )
}

const TabsList = React.forwardRef(({ className, variant, ...props }, ref) => {
  const innerRef = React.useRef(null)
  const { fade, style: edgeStyle } = useEdgeFade(innerRef)

  React.useEffect(() => {
    if (variant !== "secondaryNav") return
    const listEl = innerRef.current
    if (!listEl) return

    const scrollActiveIntoView = () => {
      const active = listEl.querySelector('[data-state="active"]')
      if (active) {
        active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      }
    }

    const observer = new MutationObserver(scrollActiveIntoView)
    observer.observe(listEl, { attributes: true, attributeFilter: ['data-state'], subtree: true })
    return () => observer.disconnect()
  }, [variant])

  // One-time nudge on mount when the strip has right-overflow. A small
  // right-then-back scroll catches the eye and reveals the chevrons briefly,
  // teaching users that the strip scrolls. Skipped on subsequent renders
  // (and for non-overflowing tab bars) so it doesn't get spammy.
  const nudgedRef = React.useRef(false)
  React.useEffect(() => {
    if (variant !== "secondaryNav") return
    if (nudgedRef.current) return
    if (!fade.right) return
    const el = innerRef.current
    if (!el) return
    nudgedRef.current = true
    const timers = []
    timers.push(setTimeout(() => {
      el.scrollBy({ left: 16, behavior: 'smooth' })
      timers.push(setTimeout(() => el.scrollBy({ left: -16, behavior: 'smooth' }), 280))
    }, 500))
    return () => timers.forEach(clearTimeout)
  }, [variant, fade.right])

  const mergedRef = React.useCallback((node) => {
    innerRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }, [ref])

  if (variant !== "secondaryNav") {
    return (
      <TabsPrimitive.List
        ref={mergedRef}
        className={cn(
          "inline-flex h-10 items-center justify-center rounded-md bg-background p-1 text-muted-foreground",
          className
        )}
        {...props}
      />
    )
  }

  // Wrap in a relative container so overflow chevrons can overlay the edges.
  // The consumer's className lands on the wrapper so external layout
  // constraints (min-w-0, flex-1, etc.) still apply at the right DOM level.
  return (
    <div className={cn("relative", className)}>
      <TabsPrimitive.List
        ref={mergedRef}
        style={edgeStyle}
        className={tabsListSecondaryNavClass}
        {...props}
      />
      {fade.left && <OverflowChevron side="left" listRef={innerRef} />}
      {fade.right && <OverflowChevron side="right" listRef={innerRef} />}
    </div>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      variant === "secondaryNav"
        ? tabsTriggerSecondaryNavClass
        : "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent, SwipeableTabsContent }
